import React, { useContext, useEffect, useState, useMemo } from 'react';
import { UserContext } from '../../UserContext';
import { API_BASE_URL } from '../../config/api';
import LogFilterBar, { useLogFilters, matchesDateRange } from '../../components/logs/LogFilterBar';

// ─── Helpers ───────────────────────────────────────────────────────────────────
const fmtTime = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
};

const fmtMs = (ms) => {
  if (ms == null) return null;
  if (ms < 1000)   return `${ms}ms`;
  if (ms < 60000)  return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const fmtCron = (expr) => {
  if (!expr) return 'Scheduled';
  const [min, hr, dom, , dow] = expr.trim().split(/\s+/);
  if (/^\*\/(\d+)$/.test(min) && hr === '*' && dom === '*') {
    const n = +min.slice(2);
    return n === 1 ? 'Every min' : `Every ${n}m`;
  }
  if (/^\*\/(\d+)$/.test(hr) && dom === '*') {
    const n = +hr.slice(2); return `Every ${n}h`;
  }
  if (dom === '*' && dow !== '*') {
    return `Weekly ${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][+dow] ?? dow}`;
  }
  if (dom === '*' && dow === '*' && hr !== '*') {
    return `Daily ${hr}:${min === '0' ? '00' : min.padStart(2,'0')}`;
  }
  return expr.length > 16 ? expr.slice(0, 16) + '…' : expr;
};

const getTrigger = (flow) => {
  const sched = flow.triggerSource?.schedule;
  if (sched?.cronExpression)
    return { type: 'schedule', label: fmtCron(sched.cronExpression) };
  const pn = (flow.version?.trigger?.settings?.pieceName || '').toLowerCase();
  const tn = (flow.version?.trigger?.settings?.triggerName || '').toLowerCase();
  if (pn.includes('schedule') || pn.includes('cron'))
    return { type: 'schedule', label: 'Scheduled' };
  if (pn.includes('webhook') || tn.includes('hook'))
    return { type: 'webhook', label: 'Webhook' };
  if (flow.version?.trigger?.type === 'EMPTY')
    return { type: 'manual', label: 'Manual' };
  const nice = tn.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return { type: 'piece', label: nice || 'Trigger' };
};

// ─── Pieces Flattening ─────────────────────────────────────────────────────────
const flattenPieces = (trigger) => {
  if (!trigger) return [];
  const pieces = [];

  const walk = (node, depth, kind) => {
    if (!node) return;

    let pieceName = node.settings?.pieceName || node.type || 'Unknown';
    if (pieceName.startsWith('@activepieces/piece-')) {
      pieceName = pieceName.replace('@activepieces/piece-', '');
    }
    const actionName = node.settings?.actionName || node.settings?.triggerName || node.name;

    pieces.push({
      name: node.name,
      displayName: node.displayName || node.name,
      pieceName,
      actionName,
      type: node.type,
      kind,
      depth
    });

    if (node.type === 'BRANCH' || node.type === 'ROUTER') {
      if (node.nextAction) walk(node.nextAction, depth, 'action');
      if (node.settings?.onSuccessAction) walk(node.settings.onSuccessAction, depth + 1, 'action');
      if (node.settings?.onFailureAction) walk(node.settings.onFailureAction, depth + 1, 'action');
      if (node.settings?.children) {
        node.settings.children.forEach(child => walk(child, depth + 1, 'action'));
      }
    } else if (node.type === 'LOOP_ON_ITEMS') {
      if (node.settings?.firstLoopAction) walk(node.settings.firstLoopAction, depth + 1, 'action');
      if (node.nextAction) walk(node.nextAction, depth, 'action');
    } else if (node.nextAction) {
      walk(node.nextAction, depth, 'action');
    }
  };

  walk(trigger, 0, 'trigger');
  return pieces;
};

// ─── Icons ─────────────────────────────────────────────────────────────────────
const Ic = ({ d, fill = 'none', size = 12, strokeWidth = 1.75, ...p }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill}
    stroke="currentColor" strokeWidth={strokeWidth}
    strokeLinecap="round" strokeLinejoin="round" {...p}>
    {d}
  </svg>
);

const IcZap = ({ size = 15 }) => (
  <Ic size={size} strokeWidth={1.75}
    d={<><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></>}/>
);

const IcRefresh = () => <Ic size={13} strokeWidth={2}
  d={<><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></>}/>;

const IcWarn = () => <Ic size={13} strokeWidth={1.75}
  d={<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>}/>;

const IcClock = ({ size = 12 }) => <Ic size={size} strokeWidth={1.75}
  d={<><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>}/>;

const IcChevron = ({ expanded }) => <Ic size={14} strokeWidth={2} className={`apw-chevron ${expanded ? 'apw-chevron--expanded' : ''}`}
  d={<><polyline points="9 18 15 12 9 6"/></>}/>;

const IcExpand = ({ size = 12 }) => <Ic size={size} strokeWidth={1.75}
  d={<><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></>}/>;

// ─── Status config ────────────────────────────────────────────────────────────
const SC = {
  RUNNING:   { dot: '#179da6', bg: '#eff6ff', fg: '#0f6b73', label: 'Running'   },
  SUCCEEDED: { dot: '#10b981', bg: '#ecfdf5', fg: '#065f46', label: 'OK'        },
  FAILED:    { dot: '#ef4444', bg: '#fef2f2', fg: '#991b1b', label: 'Failed'    },
  TIMEOUT:   { dot: '#ef4444', bg: '#fef2f2', fg: '#991b1b', label: 'Timeout'   },
  STOPPED:   { dot: '#94a3b8', bg: '#f8fafc', fg: '#475569', label: 'Stopped'   },
  PAUSED:    { dot: '#94a3b8', bg: '#f8fafc', fg: '#475569', label: 'Paused'    },
  SCHEDULED: { dot: '#64748b', bg: '#f1f5f9', fg: '#334155', label: 'Scheduled' },
  QUEUED:    { dot: '#f59e0b', bg: '#fffbeb', fg: '#92400e', label: 'Queued'    },
};

const StatusDot = ({ status }) => {
  const s = SC[status] || SC.STOPPED;
  return <span className="apw-sdot" style={{ background: s.dot }}/>;
};

const StatusBadge = ({ status }) => {
  const s = SC[status] || SC.STOPPED;
  return (
    <span className="apw-badge" style={{ background: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
};

// ─── Skeleton ──────────────────────────────────────────────────────────────────
const Skel = ({ w = '100%', h = 10 }) => (
  <span className="ap-skel" style={{ width: w, height: h, display: 'block', borderRadius: 4 }}/>
);

// ─── JSON Expand Modal ─────────────────────────────────────────────────────────
const JsonModal = ({ title, data, onClose }) => {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '12px',
        paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
        boxSizing: 'border-box',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#1e1e2e', color: '#cdd6f4',
          width: '100%', maxWidth: 900,
          height: '100%', maxHeight: 900,
          borderRadius: 10, display: 'flex', flexDirection: 'column',
          boxShadow: '0 25px 80px rgba(0,0,0,0.5)',
          overflow: 'hidden',
        }}
      >
        {/* Modal header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, padding: '10px 14px', borderBottom: '1px solid #313244',
          flexShrink: 0,
        }}>
          <span style={{
            fontWeight: 600, fontSize: 13, color: '#cba6f7', letterSpacing: '0.02em',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {title}
          </span>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: '#313244', border: 'none', cursor: 'pointer',
              color: '#cdd6f4', fontSize: 18, lineHeight: 1,
              width: 36, height: 36, minWidth: 36, flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 8,
            }}
            title="Close (Esc)"
          >✕</button>
        </div>
        {/* Modal body */}
        <div style={{
          flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch',
          padding: '14px 18px',
        }}>
          <pre style={{
            margin: 0, fontSize: 12.5, fontFamily: "'JetBrains Mono','Fira Code',monospace",
            whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.6,
            color: '#cdd6f4',
          }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
};

// ─── Health Banner ─────────────────────────────────────────────────────────────
const HealthBanner = ({ flows, lastRun, flowStat }) => {
  const enabled = flows.filter(f => f.status === 'ENABLED');

  const failing = enabled.filter(f => {
    const lr = lastRun[f.id];
    return lr && ['FAILED', 'TIMEOUT'].includes(lr.status);
  });

  const recovered = enabled.filter(f => {
    const lr = lastRun[f.id];
    const st = flowStat[f.id];
    return lr && lr.status === 'SUCCEEDED' && st && st.f > 0;
  });

  if (failing.length > 0) {
    const names = failing.slice(0, 2).map(f => f.displayName || f.name);
    const extra = failing.length - 2;
    const label = names.join(', ') + (extra > 0 ? ` + ${extra} more` : '');
    return (
      <div className="apw-banner apw-banner--fail">
        <IcWarn/>
        <span><strong>{failing.length} flow{failing.length > 1 ? 's' : ''} failing</strong>, {label}</span>
      </div>
    );
  }

  if (recovered.length > 0) return (
    <div className="apw-banner apw-banner--warn">
      <IcWarn/>
      <span>{recovered.length} flow{recovered.length > 1 ? 's' : ''} recovered after recent failures</span>
    </div>
  );

  return null;
};

// ─── Pagination controls ───────────────────────────────────────────────────────
const Pager = ({ page, totalPages, total, pageSize, onPrev, onNext }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 12px', borderTop: '1px solid #e2e8f0',
    fontSize: 12, color: '#64748b', background: '#f8fafc', borderRadius: '0 0 6px 6px',
  }}>
    <span>{total} total entries</span>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button
        onClick={onPrev} disabled={page === 0}
        style={{
          border: '1px solid #cbd5e1', background: page === 0 ? '#f1f5f9' : '#fff',
          color: page === 0 ? '#94a3b8' : '#334155', cursor: page === 0 ? 'default' : 'pointer',
          borderRadius: 5, padding: '3px 10px', fontSize: 12,
        }}
      >‹ Prev</button>
      <span style={{ fontWeight: 500 }}>Page {page + 1} / {totalPages}</span>
      <button
        onClick={onNext} disabled={page >= totalPages - 1}
        style={{
          border: '1px solid #cbd5e1', background: page >= totalPages - 1 ? '#f1f5f9' : '#fff',
          color: page >= totalPages - 1 ? '#94a3b8' : '#334155',
          cursor: page >= totalPages - 1 ? 'default' : 'pointer',
          borderRadius: 5, padding: '3px 10px', fontSize: 12,
        }}
      >Next ›</button>
    </div>
  </div>
);

const LOGS_PAGE_SIZE = 15;

// ─── Widget ────────────────────────────────────────────────────────────────────
const ActivepiecesWidget = () => {
  const { userEmail } = useContext(UserContext);
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedRSFlowId, setExpandedRSFlowId] = useState(null);
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [selectedRunIdByFlow, setSelectedRunIdByFlow] = useState({});
  const [runDetails, setRunDetails] = useState({});
  const [fetchingRunId, setFetchingRunId] = useState(null);
  const [logsPage, setLogsPage] = useState(0);
  const { filters, setFilter, resetFilters, activeCount } = useLogFilters();

  const load = async (isRefresh = false) => {
    if (!userEmail) { setLoading(false); setError('No user email.'); return; }
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const token   = localStorage.getItem('access_token');
      const apToken = localStorage.getItem('ap_token');
      const r = await fetch(
        `${API_BASE_URL}/api/activepieces/users/${encodeURIComponent(userEmail)}/summary`,
        { headers: { Authorization: `Bearer ${token}`, ...(apToken && { 'X-AP-Token': apToken }) } }
      );

      if (!r.ok) throw new Error(r.status);
      const fetchedData = await r.json();
      console.log('Fetch summary:', JSON.stringify(fetchedData, null, 2));
      setData(fetchedData);
      setError(null);
    } catch { setError('Could not load automation data.'); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => load(true), 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  const fetchRunDetail = async (runId) => {
    if (!runId || runDetails[runId] || fetchingRunId === runId) return;
    setFetchingRunId(runId);
    try {
      const token = localStorage.getItem('access_token');
      const apToken = localStorage.getItem('ap_token');
      const r = await fetch(
        `${API_BASE_URL}/api/activepieces/runs/${runId}`,
        { headers: { Authorization: `Bearer ${token}`, ...(apToken && { 'X-AP-Token': apToken }) } }
      );
      if (!r.ok) throw new Error(r.status);
      const d = await r.json();
      setRunDetails(prev => ({ ...prev, [runId]: d }));
    } catch (e) {
      console.error('Failed to fetch run detail', e);
    } finally {
      setFetchingRunId(null);
    }
  };

  const handleRSFlowClick = (flowId, latestRunId) => {
    if (expandedRSFlowId === flowId) {
      setExpandedRSFlowId(null);
    } else {
      setExpandedRSFlowId(flowId);
      if (latestRunId) {
        setSelectedRunIdByFlow(prev => ({ ...prev, [flowId]: latestRunId }));
        fetchRunDetail(latestRunId);
      }
    }
  };

  const handleRunSelect = (flowId, runId) => {
    setSelectedRunIdByFlow(prev => ({ ...prev, [flowId]: runId }));
    fetchRunDetail(runId);
  };

  // ── Derived ───────────────────────────────────────────────────────────────────
  const runs   = data?.recent_runs      ?? [];
  const flows  = data?.flows            ?? [];

  const getFlowRuns = (flowId) => {
    return runs.filter(r => r.flowId === flowId).sort((a, b) => new Date(b.created) - new Date(a.created)).slice(0, 5);
  };
  const connCt = data?.connections_count ?? 0;

  const lastRun = {};
  [...runs].sort((a, b) => new Date(b.created) - new Date(a.created))
    .forEach(r => { if (!lastRun[r.flowId]) lastRun[r.flowId] = r; });

  const flowStat = {};
  runs.forEach(r => {
    if (!flowStat[r.flowId]) flowStat[r.flowId] = { s: 0, f: 0, total: 0 };
    flowStat[r.flowId].total++;
    if (r.status === 'SUCCEEDED') flowStat[r.flowId].s++;
    else if (['FAILED', 'TIMEOUT'].includes(r.status)) flowStat[r.flowId].f++;
  });

  const flowById = Object.fromEntries(flows.map(f => [f.id, f]));

  const getFlowName = (flowId, fallbackVersionDisplayName) =>
    flowById[flowId]?.version?.displayName ||
    fallbackVersionDisplayName ||
    flowId;

  const liveByFlow = {};
  runs
    .filter(r => r.status === 'RUNNING')
    .filter(r => flowById[r.flowId]?.status === 'ENABLED')
    .forEach(r => { if (!liveByFlow[r.flowId]) liveByFlow[r.flowId] = r; });
  const liveRuns = Object.values(liveByFlow);
  const runningFlowIds = new Set(liveRuns.map(r => r.flowId));

  const enabledCt  = flows.filter(f => f.status === 'ENABLED').length;

  const schedFlows = flows.filter(f =>
    getTrigger(f).type === 'schedule' &&
    f.status === 'ENABLED' &&
    !runningFlowIds.has(f.id)
  );

  const totalRuns   = runs.length;
  const failRuns    = runs.filter(r => ['FAILED', 'TIMEOUT'].includes(r.status)).length;

  const flowsWithActivity = flows.filter(f =>
    runningFlowIds.has(f.id) || lastRun[f.id] != null
  );
  const flowsRunningOrOk = flowsWithActivity.filter(f => {
    if (runningFlowIds.has(f.id)) return true;
    return lastRun[f.id]?.status === 'SUCCEEDED';
  }).length;
  const successRate = flowsWithActivity.length > 0
    ? Math.round((flowsRunningOrOk / flowsWithActivity.length) * 100)
    : null;

  const durRuns = runs.filter(r => r.duration != null);
  const avgDur  = durRuns.length
    ? Math.round(durRuns.reduce((a, r) => a + r.duration, 0) / durRuns.length) : null;

  const completedRuns = runs.filter(r => r.status !== 'RUNNING');

  // ── Filter options, derived from the runs actually present ───────────────────
  const flowOptions = useMemo(() => {
    const seen = new Map();
    completedRuns.forEach(r => {
      if (r.flowId && !seen.has(r.flowId)) {
        seen.set(r.flowId, getFlowName(r.flowId, r.flowVersion?.displayName));
      }
    });
    return [...seen].map(([value, label]) => ({ value, label: label || value }))
      .sort((a, b) => a.label.localeCompare(b.label));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const statusOptions = useMemo(() => {
    const seen = new Set(completedRuns.map(r => r.status).filter(Boolean));
    return [...seen].map(v => ({ value: v, label: SC[v]?.label || v }))
      .sort((a, b) => a.label.localeCompare(b.label));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // ── Apply search + filters ───────────────────────────────────────────────────
  const filteredRuns = useMemo(() => {
    const q = filters.search.trim().toLowerCase();

    return completedRuns.filter(r => {
      if (filters.agent && r.flowId !== filters.agent) return false;
      if (filters.status && r.status !== filters.status) return false;
      if (!matchesDateRange(r.startTime || r.created, filters.dateFrom, filters.dateTo)) return false;

      if (!q) return true;
      const haystack = [
        getFlowName(r.flowId, r.flowVersion?.displayName),
        r.flowId,
        r.id,
        r.status,
        SC[r.status]?.label,
      ];
      return haystack.some(v => v && String(v).toLowerCase().includes(q));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filters]);

  const totalLogPages = Math.max(1, Math.ceil(filteredRuns.length / LOGS_PAGE_SIZE));
  // Filtering can shrink the list below the current page, clamp instead of
  // rendering an empty page.
  const safePage = Math.min(logsPage, totalLogPages - 1);
  const pagedRuns = filteredRuns.slice(safePage * LOGS_PAGE_SIZE, (safePage + 1) * LOGS_PAGE_SIZE);

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="dash-card apw-card">
      <div className="apw-head">
        <div className="apw-head-left">
          <span className="apw-eyebrow">Automation</span>
          <Skel w="160px" h={14}/>
        </div>
      </div>
      <div className="apw-body">
        <Skel w="100%" h={36}/>
        <Skel w="100%" h={28}/>
        <Skel w="100%" h={88}/>
        <Skel w="100%" h={64}/>
      </div>
    </div>
  );

  // ── Error ─────────────────────────────────────────────────────────────────────
  if (error || !data) return (
    <div className="dash-card apw-card">
      <div className="apw-head">
        <div className="apw-head-left">
          <span className="apw-eyebrow">Automation</span>
          <span className="apw-title">Workflow Overview</span>
        </div>
      </div>
      <div className="apw-body">
        <p className="apw-empty">{error || 'No data.'}</p>
      </div>
    </div>
  );

  // ── Full ──────────────────────────────────────────────────────────────────────
  return (
    <div className="dash-card apw-card">

      {/* ── Header ── */}
      <div className="apw-head">
        <div className="apw-head-left">
          <span className="apw-eyebrow">Automation</span>
          <div className="apw-title">
            <IcZap size={14}/>
            Workflow Overview
            {liveRuns.length > 0 && (
              <span className="apw-live-badge">
                <span className="apw-live-dot"/>
                {liveRuns.length} live
              </span>
            )}
            {failRuns > 0 && (
              <span className="apw-fail-badge">
                <IcWarn/>
                {failRuns} failed
              </span>
            )}
          </div>
        </div>
        <button
          className={`apw-refresh-btn${refreshing ? ' apw-refresh-btn--spin' : ''}`}
          onClick={() => load(true)} title="Refresh"
        >
          <IcRefresh/>
        </button>
      </div>

      <div className="apw-body">

        {/* ── KPI strip ── */}
        <div className="apw-kpi-strip">
          <div className="apw-kpi-cell">
            <span className="apw-kpi-val">{flows.length}</span>
            <span className="apw-kpi-lbl">Flows</span>
            {flows.length > 0 && (
              <div className="apw-mini-bar">
                <div className="apw-mini-bar-fill apw-mini-bar-fill--blue"
                  style={{ width: `${(enabledCt / flows.length) * 100}%` }}/>
              </div>
            )}
          </div>
          <div className="apw-kpi-cell">
            <span className="apw-kpi-val apw-kpi-val--green">{enabledCt}</span>
            <span className="apw-kpi-lbl">Active</span>
            {flows.length > 0 && (
              <div className="apw-mini-bar">
                <div className="apw-mini-bar-fill apw-mini-bar-fill--green"
                  style={{ width: `${(enabledCt / flows.length) * 100}%` }}/>
              </div>
            )}
          </div>
          <div className="apw-kpi-cell">
            <span className={`apw-kpi-val ${successRate !== null && successRate < 80 ? 'apw-kpi-val--red' : 'apw-kpi-val--green'}`}>
              {successRate !== null ? `${successRate}%` : '-'}
            </span>
            <span className="apw-kpi-lbl">Success</span>
            {successRate !== null && (
              <div className="apw-mini-bar">
                <div className={`apw-mini-bar-fill ${successRate < 80 ? 'apw-mini-bar-fill--red' : 'apw-mini-bar-fill--green'}`}
                  style={{ width: `${successRate}%` }}/>
              </div>
            )}
          </div>
          <div className="apw-kpi-cell">
            <span className={`apw-kpi-val ${failRuns > 0 ? 'apw-kpi-val--red' : ''}`}>{failRuns}</span>
            <span className="apw-kpi-lbl">Failed</span>
            <div className="apw-mini-bar">
              <div className="apw-mini-bar-fill apw-mini-bar-fill--red"
                style={{ width: totalRuns > 0 ? `${(failRuns / totalRuns) * 100}%` : '0%' }}/>
            </div>
          </div>
          {avgDur != null && (
            <div className="apw-kpi-cell">
              <span className="apw-kpi-val">{fmtMs(avgDur)}</span>
              <span className="apw-kpi-lbl">Avg Run</span>
              <div className="apw-mini-bar" style={{ visibility: 'hidden' }}/>
            </div>
          )}
          {connCt > 0 && (
            <div className="apw-kpi-cell">
              <span className="apw-kpi-val">{connCt}</span>
              <span className="apw-kpi-lbl">Apps</span>
              <div className="apw-mini-bar" style={{ visibility: 'hidden' }}/>
            </div>
          )}
        </div>

        {/* ── Health banner ── */}
        <HealthBanner flows={flows} lastRun={lastRun} flowStat={flowStat}/>

        {/* ── Running & Scheduled ── */}
        {(liveRuns.length > 0 || schedFlows.length > 0) && (
          <section className="apw-section">
            <div className="apw-sec-head">
              <span className="apw-sec-title">Running &amp; Scheduled</span>
              <span className="apw-sec-hint">
                {liveRuns.length > 0 && `${liveRuns.length} live`}
                {liveRuns.length > 0 && schedFlows.length > 0 && ' · '}
                {schedFlows.length > 0 && `${schedFlows.length} scheduled`}
              </span>
            </div>
            <div className="apw-list-scroll">
              <table className="apw-table">
                <tbody>
                  {liveRuns.map(run => {
                    const flow = flowById[run.flowId];
                    const expanded = expandedRSFlowId === run.flowId;
                    const recentRunsForFlow = getFlowRuns(run.flowId);
                    const selectedRunId = selectedRunIdByFlow[run.flowId] || run.id;
                    const selectedRunDetail = runDetails[selectedRunId];
                    const flowName = getFlowName(run.flowId, run.flowVersion?.displayName);
                    return (
                      <React.Fragment key={`rs-live-${run.id}`}>
                        <tr className="apw-row apw-row--live apw-row--clickable" onClick={() => handleRSFlowClick(run.flowId, run.id)} aria-expanded={expanded}>
                          <td className="apw-col-dot"><span className="apw-sdot apw-sdot--pulse" style={{ background: '#179da6' }}/></td>
                          <td className="apw-col-name">
                            <div className="apw-name-wrap">
                              <IcChevron expanded={expanded} />
                              <div>
                                {flowName}
                                <div className="apw-flow-id">{run.flowId}</div>
                              </div>
                            </div>
                          </td>
                          <td className="apw-col-meta">Running · started {fmtTime(run.startTime)}</td>
                          <td className="apw-col-badge"><StatusBadge status="RUNNING"/></td>
                        </tr>
                        {expanded && flow?.version?.trigger && (
                          <FlowPiecesTree
                            flow={flow}
                            recentRuns={recentRunsForFlow}
                            selectedRunId={selectedRunId}
                            onSelectRun={handleRunSelect}
                            runDetail={selectedRunDetail}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                  {schedFlows.map(flow => {
                    const tri    = getTrigger(flow);
                    const last   = lastRun[flow.id];
                    const isFail = last && ['FAILED', 'TIMEOUT'].includes(last.status);
                    const name   = flow.version?.displayName || flow.displayName || flow.name || flow.id;
                    const expanded = expandedRSFlowId === flow.id;
                    const recentRunsForFlow = getFlowRuns(flow.id);
                    const selectedRunId = selectedRunIdByFlow[flow.id] || last?.id;
                    const selectedRunDetail = runDetails[selectedRunId];

                    return (
                      <React.Fragment key={`rs-sched-${flow.id}`}>
                        <tr className={`apw-row apw-row--live apw-row--clickable${isFail ? ' apw-row--fail' : ''}`} onClick={() => handleRSFlowClick(flow.id, last?.id)} aria-expanded={expanded}>
                          <td className="apw-col-dot">
                            <span className="apw-sdot apw-sdot--pulse" style={{ background: '#179da6' }}/>
                          </td>
                          <td className="apw-col-name">
                            <div className="apw-name-wrap">
                              <IcChevron expanded={expanded} />
                              <div>
                                {name}
                                <div className="apw-flow-id">{flow.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="apw-col-meta">
                            <IcClock size={10}/>&nbsp;{tri.label}
                            {last && ` · last ${fmtTime(last.startTime)}`}
                          </td>
                          <td className="apw-col-badge">
                            <StatusBadge status="RUNNING"/>
                          </td>
                        </tr>
                        {expanded && flow?.version?.trigger && (
                          <FlowPiecesTree
                            flow={flow}
                            recentRuns={recentRunsForFlow}
                            selectedRunId={selectedRunId}
                            onSelectRun={handleRunSelect}
                            runDetail={selectedRunDetail}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ── Workflow Logs (previously "Recent Runs") ── */}
        {completedRuns.length > 0 && (
          <section className="apw-section">
            <div className="apw-sec-head">
              <span className="apw-sec-title">Workflow Logs</span>
              <span className="apw-sec-hint">{completedRuns.length} total · click to inspect</span>
            </div>

            <LogFilterBar
              filters={filters}
              setFilter={(k, v) => { setFilter(k, v); setLogsPage(0); setExpandedRunId(null); }}
              onReset={() => { resetFilters(); setLogsPage(0); setExpandedRunId(null); }}
              activeCount={activeCount}
              searchPlaceholder="Search workflow name, run ID, status…"
              selects={[
                { key: 'agent',  label: 'Workflow', options: flowOptions,   allLabel: 'All workflows' },
                { key: 'status', label: 'Status',   options: statusOptions, allLabel: 'All statuses'  },
              ]}
              resultLabel={`${filteredRuns.length} of ${completedRuns.length} runs`}
            />

            <div className="apw-list-scroll" style={{ maxHeight: 600 }}>
              <table className="apw-table">
                <tbody>
                  {pagedRuns.map(run => {
                    const isFail = ['FAILED', 'TIMEOUT'].includes(run.status);
                    const flowName = getFlowName(run.flowId, run.flowVersion?.displayName) || '-';
                    const flow = flowById[run.flowId];
                    const expanded = expandedRunId === run.id;
                    const recentRunsForFlow = getFlowRuns(run.flowId);
                    const selectedRunId = selectedRunIdByFlow[run.flowId] || run.id;
                    const runDetail = runDetails[selectedRunId];

                    const handleClick = () => {
                      if (expandedRunId === run.id) {
                        setExpandedRunId(null);
                      } else {
                        setExpandedRunId(run.id);
                        setSelectedRunIdByFlow(prev => ({ ...prev, [run.flowId]: run.id }));
                        fetchRunDetail(run.id);
                      }
                    };

                    return (
                      <React.Fragment key={run.id}>
                        <tr className={`apw-row apw-row--clickable${isFail ? ' apw-row--fail' : ''}`} onClick={handleClick} aria-expanded={expanded}>
                          <td className="apw-col-dot"><StatusDot status={run.status}/></td>
                          <td className="apw-col-name">
                            <div className="apw-name-wrap">
                              <IcChevron expanded={expanded}/>
                              <div>
                                {flowName}
                                <div className="apw-flow-id">{run.flowId}</div>
                              </div>
                            </div>
                          </td>
                          <td className="apw-col-meta">
                            {run.duration != null && `${fmtMs(run.duration)} · `}
                            {fmtTime(run.startTime || run.created)}
                          </td>
                          <td className="apw-col-badge"><StatusBadge status={run.status}/></td>
                        </tr>
                        {expanded && flow?.version?.trigger && (
                          <FlowPiecesTree
                            flow={flow}
                            recentRuns={recentRunsForFlow}
                            selectedRunId={selectedRunId}
                            onSelectRun={(flowId, rid) => {
                              setSelectedRunIdByFlow(prev => ({ ...prev, [flowId]: rid }));
                              fetchRunDetail(rid);
                            }}
                            runDetail={runDetail}
                            detailMode="output-only"
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              {filteredRuns.length === 0 && (
                <p className="apw-empty">No runs match these filters.</p>
              )}
            </div>
            {/* Pagination footer */}
            {filteredRuns.length > 0 && (
              <Pager
                page={safePage}
                totalPages={totalLogPages}
                total={filteredRuns.length}
                pageSize={LOGS_PAGE_SIZE}
                onPrev={() => { setLogsPage(Math.max(0, safePage - 1)); setExpandedRunId(null); }}
                onNext={() => { setLogsPage(Math.min(totalLogPages - 1, safePage + 1)); setExpandedRunId(null); }}
              />
            )}
          </section>
        )}

        {/* ── Empty ── */}
        {flows.length === 0 && runs.length === 0 && (
          <p className="apw-empty">No flows configured yet.</p>
        )}
        {flows.length > 0 && runs.length === 0 && (
          <p className="apw-empty">Flows will appear here once they run.</p>
        )}
      </div>
    </div>
  );
};

// detailMode: 'none'        = pieces structure only
//             'output-only' = output + error per step (Workflow Logs)
//             'full'        = input + output + error (Running & Scheduled)
const FlowPiecesTree = ({ flow, recentRuns, selectedRunId, onSelectRun, runDetail, detailMode = 'full' }) => {
  const pieces = flattenPieces(flow.version?.trigger);
  const [expandedStep, setExpandedStep] = useState(null);
  const [modal, setModal] = useState(null); // { title, data }

  const showDetail = detailMode !== 'none';

  const selectedRun = recentRuns?.find(r => r.id === selectedRunId);
  const hasSteps = runDetail?.steps && Object.keys(runDetail.steps).length > 0;
  const isQueued = selectedRun?.status === 'QUEUED';

  return (
    <tr className="apw-pieces-row">
      <td colSpan="4" className="apw-pieces-cell">

        {/* JSON expand modal */}
        {modal && (
          <JsonModal title={modal.title} data={modal.data} onClose={() => setModal(null)}/>
        )}

        {/* ── Selected run context bar ── */}
        {showDetail && selectedRun && (
          <div className="apw-run-context">
            <StatusBadge status={selectedRun.status}/>
            {selectedRun.startTime && <span className="apw-run-context-time"><IcClock size={10}/>&nbsp;Started {fmtTime(selectedRun.startTime)}</span>}
            {selectedRun.finishTime && selectedRun.startTime && <span className="apw-run-context-time">· Finished {fmtTime(selectedRun.finishTime)}</span>}
            {selectedRun.duration != null && <span className="apw-run-context-time">· {fmtMs(selectedRun.duration)}</span>}
          </div>
        )}

        {/* ── Step logs notices ── */}
        {showDetail && isQueued && (
          <div className="apw-no-logs">Run is queued, step logs will appear once execution starts.</div>
        )}
        {showDetail && !isQueued && selectedRunId && !hasSteps && (
          <div className="apw-no-logs">No step logs available for this run.</div>
        )}

        {/* ── Pieces tree ── */}
        <div className="apw-pieces-tree">
          {pieces.map((piece, i) => {
            const stepResult = showDetail ? runDetail?.steps?.[piece.name] : null;
            let status = 'STOPPED';
            if (stepResult) {
              if (stepResult.status === 'SUCCEEDED') status = 'SUCCEEDED';
              else if (stepResult.status === 'FAILED') status = 'FAILED';
              else if (stepResult.status === 'RUNNING') status = 'RUNNING';
            }
            const canExpand = showDetail && !!stepResult;
            const isExpanded = expandedStep === piece.name;
            const showOutput = (detailMode === 'full' || detailMode === 'output-only') && stepResult?.output !== undefined;

            return (
              <div key={piece.name} className="apw-piece-wrap" style={{ paddingLeft: `${(piece.depth || 0) * 20}px` }}>
                <div
                  className={`apw-piece-item ${canExpand ? 'apw-piece-item--has-result' : ''}`}
                  onClick={() => canExpand && setExpandedStep(isExpanded ? null : piece.name)}
                >
                  <div className="apw-piece-connector">
                    {i === 0 ? '' : i === pieces.length - 1 ? '└─' : '├─'}
                  </div>
                  <div className="apw-piece-dot-wrap">
                    {stepResult ? <StatusDot status={status}/> : <div className="apw-piece-dot-empty"/>}
                  </div>
                  <div className="apw-piece-info">
                    <div className="apw-piece-name">{piece.displayName}</div>
                    <div className="apw-piece-sub">{piece.pieceName} · {piece.actionName}</div>
                  </div>
                  {canExpand && (
                    <div className="apw-piece-result">
                      {stepResult.duration != null && <span className="apw-piece-duration">{fmtMs(stepResult.duration)}</span>}
                      <StatusBadge status={status}/>
                      <IcChevron expanded={isExpanded}/>
                    </div>
                  )}
                </div>

                {isExpanded && stepResult && (
                  <div className="apw-piece-detail">
                    {stepResult.errorMessage && (
                      <div className="apw-detail-section apw-detail-section--error">
                        <div className="apw-detail-title" style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                          <span>Error</span>
                          <button
                            className="apw-expand-btn"
                            title="Expand in overlay"
                            onClick={e => { e.stopPropagation(); setModal({ title: `${piece.displayName}, Error`, data: stepResult.errorMessage }); }}
                            style={{ background:'none', border:'none', cursor:'pointer', color:'inherit', display:'flex', alignItems:'center', gap:4, fontSize:11 }}
                          ><IcExpand size={11}/> Expand</button>
                        </div>
                        <pre className="apw-detail-code">{stepResult.errorMessage}</pre>
                      </div>
                    )}
  
                      <div className="apw-detail-section">
                        <div className="apw-detail-title" style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                          <span>Input</span>
                          <button
                            className="apw-expand-btn"
                            title="Expand in overlay"
                            onClick={e => { e.stopPropagation(); setModal({ title: `${piece.displayName}, Input`, data: stepResult.input }); }}
                            style={{ background:'none', border:'none', cursor:'pointer', color:'inherit', display:'flex', alignItems:'center', gap:4, fontSize:11 }}
                          ><IcExpand size={11}/> Expand</button>
                        </div>
                        <pre className="apw-detail-code">{JSON.stringify(stepResult.input, null, 2)}</pre>
                      </div>
     
                    {showOutput && (
                      <div className="apw-detail-section">
                        <div className="apw-detail-title" style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                          <span>Output</span>
                          <button
                            className="apw-expand-btn"
                            title="Expand in overlay"
                            onClick={e => { e.stopPropagation(); setModal({ title: `${piece.displayName}, Output`, data: stepResult.output }); }}
                            style={{ background:'none', border:'none', cursor:'pointer', color:'inherit', display:'flex', alignItems:'center', gap:4, fontSize:11 }}
                          ><IcExpand size={11}/> Expand</button>
                        </div>
                        <pre className="apw-detail-code">{JSON.stringify(stepResult.output, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </td>
    </tr>
  );
};

export default ActivepiecesWidget;
